import React, { memo } from 'react';
import { StyleSheet, View, Modal } from 'react-native';

import { LoadingAnimation, theme, useResponsive } from '@sg/garnish';

import { useTrackEventEffect } from '@order/Telemetry';

import { LocationSearch, PageWrapper, PermissionsPrimer } from '../../components';
import { useLocationPermissionPrimer, usePushNotificationPermissionPrimer } from '../../hooks';
import { useReorderState } from '../ReorderingScreen';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsScreen = memo(() => {
  const { currentBreakpoint } = useResponsive();
  const {
    reordering: isReordering,
    restaurantSlug: reorderRestaurantSlug,

    deliveryOrderDetail,
  } = useReorderState();

  const pushPrimer = usePushNotificationPermissionPrimer({
    variants: LOCATIONS_SCREEN_PUSH_VARIANTS,
  });
  const locationPrimer = useLocationPermissionPrimer({
    variants: LOCATIONS_SCREEN_LOCATION_VARIANTS,
  });

  const isLoading =
    pushPrimer.isPushNotificationPermissionsLoading || locationPrimer.isLocationPermissionsLoading;
  const isPrimerActive = pushPrimer.isPrimerActive || locationPrimer.isPrimerActive;

  const interactedLocationRestaurantSlug = isReordering ? reorderRestaurantSlug : undefined;
  const interactedLocationDeliveryAddressId = isReordering
    ? deliveryOrderDetail?.addressId
    : undefined;

  const shouldRequestLocationPermission = !(
    locationPrimer.isLocationPermissionDenied ||
    locationPrimer.isLocationPermissionSkipped ||
    locationPrimer.isLocationPermissionAlreadyShown
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({ name: 'location.view' });

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  if (isPrimerActive) {
    return (
      <Modal visible>
        <PermissionsPrimer pushPrimer={pushPrimer} locationPrimer={locationPrimer} />
      </Modal>
    );
  }

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.wrapper}>
        <LocationSearch
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
          shouldRequestLocationPermission={shouldRequestLocationPermission}
        />
      </View>
    );
  }

  return (
    <PageWrapper contentContainerStyle={styles.wrapper}>
      <LocationSearch
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      />
    </PageWrapper>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    height: '100%',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATIONS_SCREEN_LOCATION_VARIANTS: LocationPrimerVariants = ['B', 'C'];
const LOCATIONS_SCREEN_PUSH_VARIANTS: PushPrimerVariants = ['B'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationPrimerVariants = Parameters<typeof useLocationPermissionPrimer>[0]['variants'];
type PushPrimerVariants = Parameters<typeof usePushNotificationPermissionPrimer>[0]['variants'];
